import React from 'react';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';

interface LanguageSelectorProps {
  variant?: 'compact' | 'full';
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'full' }) => {
  // Safe language context access with fallback
  let language: Language;
  let setLanguage: (lang: Language) => void;
  let t: (key: string) => string;
  
  try {
    const langContext = useLanguage();
    language = langContext.language;
    setLanguage = langContext.setLanguage;
    t = langContext.t;
  } catch (error) {
    console.warn('LanguageContext not ready, using fallback');
    language = 'th';
    setLanguage = () => {};
    t = (key: string) => {
      const fallbacks: Record<string, string> = {
        'languages.th': 'ไทย',
        'languages.en': 'English',
        'languages.lo': 'ລາວ'
      };
      return fallbacks[key] || '';
    };
  }

  const languages = [
    { code: 'th' as Language, name: t('languages.th'), flag: '🇹🇭' },
    { code: 'en' as Language, name: t('languages.en'), flag: '🇺🇸' },
    { code: 'lo' as Language, name: t('languages.lo'), flag: '🇱🇦' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="relative gap-2 px-3 py-2 backdrop-blur-xl bg-background/50 border-primary/30 hover:border-primary/60 hover:bg-primary/10 transition-all duration-300"
          style={{
            boxShadow: "0 0 20px rgba(100, 100, 255, 0.2)"
          }}
        >
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{currentLanguage?.flag}</span>
          <span className="text-xs text-muted-foreground">{currentLanguage?.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 backdrop-blur-xl bg-card/95 border-primary/30 z-[100]"
        style={{
          boxShadow: "0 0 40px rgba(100, 100, 255, 0.3), 0 8px 32px rgba(0, 0, 0, 0.2)"
        }}
      >
        {languages.map(lang => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className={`flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-all duration-200 ${
              language === lang.code 
                ? 'bg-primary/20 text-primary font-medium' 
                : 'hover:bg-primary/10'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">{lang.flag}</span>
              <span className="text-sm">{lang.name}</span>
            </div>
            {language === lang.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};